import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import LoadingScreen from '../components/ui/LoadingScreen';
import { IconPlus } from '../components/ui/Icons';
import { useToast } from '../contexts/ToastContext';
import { usePortal } from '../contexts/PortalContext';
import { copyText } from '../utils/portalMode';
import { getTenantId } from '../utils/ids';

function Partners() {
  const toast = useToast();
  const { partnerPortalOrigin, partnerPortalHost } = usePortal();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const response = await api.post('/tenant/list', { page: 1, limit: 500, kind: 'partner' });
        setPartners(response.data.data?.tenants || []);
      } catch (error) {
        toast.error('Failed to fetch partners');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [toast]);

  const handleCopy = async () => {
    const ok = await copyText(partnerPortalOrigin);
    toast[ok ? 'success' : 'error'](ok ? 'Partner portal URL copied' : 'Could not copy URL');
  };

  if (loading) {
    return <LoadingScreen message="Loading partners..." />;
  }

  return (
    <div className="page">
      <PageHeader
        title="Partners"
        description="Reseller tenants. They log in at the shared partner portal URL."
        action={
          <Link to="/tenants" className="btn btn--primary">
            <IconPlus width={18} height={18} />
            Create in Tenants
          </Link>
        }
      />

      <div className="card portal-url-card">
        <div>
          <div className="stat-card__label">Partner portal URL</div>
          <div className="portal-url">{partnerPortalOrigin || partnerPortalHost || 'Not configured'}</div>
        </div>
        <div className="btn-group">
          <button type="button" className="btn btn--secondary" onClick={handleCopy} disabled={!partnerPortalOrigin}>
            Copy URL
          </button>
          <Link to="/platform-settings" className="btn btn--ghost">
            Change
          </Link>
        </div>
      </div>

      <div className="card">
        {partners.length === 0 ? (
          <EmptyState
            title="No partners yet"
            description="Create a tenant with kind Partner, then invite a partner admin."
          />
        ) : (
          <div className="table-wrapper">
            <table className="data-table data-table--responsive">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Subdomain</th>
                  <th>Brand</th>
                  <th>Wholesale</th>
                  <th>Default retail</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((partner) => (
                  <tr key={getTenantId(partner)}>
                    <td data-label="Name">{partner.name}</td>
                    <td data-label="Subdomain">{partner.subdomain}</td>
                    <td data-label="Brand">{partner.brand?.appName || '—'}</td>
                    <td data-label="Wholesale">{partner.billing?.platformBillsPartner === false ? 'No' : 'Yes'}</td>
                    <td data-label="Default retail">{partner.billing?.defaultRetailCollector || 'partner'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Partners;
